import { getMyAccess } from '@/api/access'
import { can, canAny, canAll, normalizeActionCode } from '@/utils/access'
import { FULL_CATALOG_ROLLOUT_REVISION } from '@/router/iam-v2-route-contract'
import { isFullCatalogRouteSource as hasFullCatalogRouteSource } from '@/router/iam-v2-route-source'

const STATUS_IDLE = 'idle'
const STATUS_LOADING = 'loading'
const STATUS_READY = 'ready'
const STATUS_ERROR = 'error'
const ROLE_CODE_PATTERN = /^[a-z][a-z0-9_]*(?:[.-][a-z0-9_]+)*$/
const ROUTE_CODE_PATTERN = /^route\.[a-z][a-z0-9_]*$/
const DIGEST_PATTERN = /^[a-f0-9]{64}$/
const ROUTE_VISIBILITY_MODES = ['ACTION_ANY', 'ACTION_ALL', 'CHILDREN', 'ROOT']
const ROLLOUT_MODES = ['legacy', 'dual', 'v2']
const ROLLOUT_FIELDS = ['mode', 'revision', 'enforced_action_codes', 'enforced_route_codes']
const ROLLOUT_SET_FIELDS = ['contract_version', 'registry_revision', 'rollouts']
const ROUTE_SOURCE_FIELDS = ['mode', 'revision']
const ROLLOUT_SET_CONTRACT_VERSION = 'iam-v2-rollout-set/v1'
const LEGACY_ROLLOUT_SET_REGISTRY_REVISION = 'stage9-read-rollouts-v1'
const STAGE10_ROLLOUT_SET_REGISTRY_REVISION = 'stage10-pricing-global-rollouts-v2'
const ROLLOUT_SET_REGISTRY_REVISION = 'stage11-full-capability-rollouts-v1'
const STAGE7_ROLLOUT_REVISION = 'stage7-exchange-rate-read'
const STAGE8_ROLLOUT_REVISION = 'stage8-product-category-read'
const STAGE9_CONTENT_ROLLOUT_REVISION = 'stage9-content-manual-read'
const STAGE9_COLLECTION_ROLLOUT_REVISION = 'stage9-collection-tool-download'
const STAGE9_PRICING_ROLLOUT_REVISION = 'stage9b-amazon-catalog-pricing-read'
const STAGE10_GLOBAL_PRICING_ROLLOUT_REVISION = 'stage10-amazon-catalog-pricing-global-read'
const STAGE7_ACTION_CODES = ['system.exchange_rate.read']
const STAGE7_ROUTE_CODES = ['route.system_exchange_rate']
const STAGE8_ACTION_CODES = ['product.category.read']
const STAGE8_ROUTE_CODES = []
const STAGE9_CONTENT_ACTION_CODES = ['content.manual.read']
const STAGE9_COLLECTION_ACTION_CODES = ['product.collection_tool.download']
const STAGE9_PRICING_ACTION_CODES = ['amazon.catalog_pricing.read']
const STAGE9_PRICING_ROUTE_CODES = ['route.amazon_catalog_pricing']
const COMPATIBILITY_ROLLOUT_REVISIONS = [STAGE7_ROLLOUT_REVISION, STAGE8_ROLLOUT_REVISION]
const LEGACY_ROLLOUT_SET_REVISIONS = [
  STAGE7_ROLLOUT_REVISION,
  STAGE8_ROLLOUT_REVISION,
  STAGE9_CONTENT_ROLLOUT_REVISION,
  STAGE9_COLLECTION_ROLLOUT_REVISION,
  STAGE9_PRICING_ROLLOUT_REVISION
]
const ROLLOUT_SET_REVISIONS = [
  STAGE7_ROLLOUT_REVISION,
  STAGE8_ROLLOUT_REVISION,
  STAGE9_CONTENT_ROLLOUT_REVISION,
  STAGE9_COLLECTION_ROLLOUT_REVISION,
  STAGE10_GLOBAL_PRICING_ROLLOUT_REVISION
]
const ROLLOUT_SET_SCHEMAS = {
  [LEGACY_ROLLOUT_SET_REGISTRY_REVISION]: LEGACY_ROLLOUT_SET_REVISIONS,
  [STAGE10_ROLLOUT_SET_REGISTRY_REVISION]: ROLLOUT_SET_REVISIONS,
  [ROLLOUT_SET_REGISTRY_REVISION]: ROLLOUT_SET_REVISIONS
}
const ROLLOUT_POLICIES = {
  [STAGE7_ROLLOUT_REVISION]: {
    actionCodes: STAGE7_ACTION_CODES,
    routeCodes: STAGE7_ROUTE_CODES,
    allowedDataScopes: [1],
    pairActionAndRoute: true
  },
  [STAGE8_ROLLOUT_REVISION]: {
    actionCodes: STAGE8_ACTION_CODES,
    routeCodes: STAGE8_ROUTE_CODES,
    allowedDataScopes: [1, 2, 3, 4],
    pairActionAndRoute: false
  },
  [STAGE9_CONTENT_ROLLOUT_REVISION]: {
    actionCodes: STAGE9_CONTENT_ACTION_CODES,
    routeCodes: [],
    allowedDataScopes: [1, 2, 3, 4],
    pairActionAndRoute: false
  },
  [STAGE9_COLLECTION_ROLLOUT_REVISION]: {
    actionCodes: STAGE9_COLLECTION_ACTION_CODES,
    routeCodes: [],
    allowedDataScopes: [1, 2, 3, 4],
    pairActionAndRoute: false
  },
  [STAGE9_PRICING_ROLLOUT_REVISION]: {
    actionCodes: STAGE9_PRICING_ACTION_CODES,
    routeCodes: STAGE9_PRICING_ROUTE_CODES,
    allowedDataScopes: [1, 2, 3],
    pairActionAndRoute: true
  },
  [STAGE10_GLOBAL_PRICING_ROLLOUT_REVISION]: {
    actionCodes: STAGE9_PRICING_ACTION_CODES,
    routeCodes: STAGE9_PRICING_ROUTE_CODES,
    allowedDataScopes: [1, 2, 3, 4],
    pairActionAndRoute: true
  }
}

let inFlightRequest = null

function accessError (message, code) {
  const error = new Error(message || 'IAM V2 access snapshot is unavailable')
  error.code = code || 'IAM_V2_ACCESS_UNAVAILABLE'
  return error
}

function positiveInteger (value) {
  const number = Number(value)
  return Number.isSafeInteger(number) && number > 0 ? number : 0
}

function nonNegativeInteger (value) {
  const number = Number(value)
  return Number.isSafeInteger(number) && number >= 0 ? number : -1
}

function currentUnixTime () {
  return Math.floor(Date.now() / 1000)
}

function normalizedCodeList (values, normalize, field) {
  if (!Array.isArray(values)) throw accessError(`${field} must be an array`, 'IAM_V2_ACCESS_INVALID')
  const seen = Object.create(null)
  const result = []
  values.forEach(value => {
    const code = normalize(value)
    if (!code || seen[code]) throw accessError(`${field} contains an invalid or duplicate code`, 'IAM_V2_ACCESS_INVALID')
    seen[code] = true
    result.push(code)
  })
  return result.sort()
}

function normalizeRoleCode (value) {
  const code = typeof value === 'string' ? value.trim() : ''
  return ROLE_CODE_PATTERN.test(code) && code.length <= 64 ? code : ''
}

function normalizeRouteTree (routes) {
  if (!Array.isArray(routes)) throw accessError('routes must be an array', 'IAM_V2_ACCESS_INVALID')
  const seen = Object.create(null)

  function visit (node, parentCode) {
    if (!node || typeof node !== 'object' || Array.isArray(node)) {
      throw accessError('route node is invalid', 'IAM_V2_ACCESS_INVALID')
    }
    const code = typeof node.code === 'string' ? node.code.trim() : ''
    const declaredParent = typeof node.parent_code === 'string' ? node.parent_code.trim() : ''
    if (!ROUTE_CODE_PATTERN.test(code) || seen[code] || declaredParent !== parentCode) {
      throw accessError('route tree identity is invalid', 'IAM_V2_ACCESS_INVALID')
    }
    if (!ROUTE_VISIBILITY_MODES.includes(node.visibility_mode) ||
      typeof node.path !== 'string' || typeof node.component !== 'string' ||
      typeof node.redirect !== 'string' || typeof node.name !== 'string' ||
      typeof node.title !== 'string' || typeof node.icon !== 'string' ||
      typeof node.hidden !== 'boolean' || typeof node.keep_alive !== 'boolean' ||
      nonNegativeInteger(node.priority) < 0 || !Array.isArray(node.children)) {
      throw accessError('route tree metadata is invalid', 'IAM_V2_ACCESS_INVALID')
    }
    seen[code] = true
    return {
      code,
      parent_code: declaredParent,
      path: node.path,
      component: node.component,
      redirect: node.redirect,
      name: node.name,
      title: node.title,
      icon: node.icon,
      hidden: node.hidden,
      keep_alive: node.keep_alive,
      priority: Number(node.priority),
      visibility_mode: node.visibility_mode,
      children: node.children.map(child => visit(child, code))
    }
  }

  return routes.map(route => visit(route, ''))
}

function flattenRouteCodes (routes, result) {
  const codes = result || Object.create(null)
  ;(routes || []).forEach(route => {
    codes[route.code] = true
    flattenRouteCodes(route.children, codes)
  })
  return codes
}

function onlyKnownCodes (codes, allowed) {
  return codes.every(code => allowed.indexOf(code) !== -1)
}

function normalizeAuthorizationRollout (value, actionCodes, routes, dataScope, expectedRevision, field) {
  const fieldName = field || 'authorization_rollout'
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw accessError(`${fieldName} must be an object`, 'IAM_V2_ACCESS_INVALID')
  }
  const fields = Object.keys(value).sort()
  if (fields.length !== ROLLOUT_FIELDS.length ||
    fields.some((field, index) => field !== ROLLOUT_FIELDS.slice().sort()[index])) {
    throw accessError(`${fieldName} fields are invalid`, 'IAM_V2_ACCESS_INVALID')
  }

  const mode = typeof value.mode === 'string' ? value.mode.trim() : ''
  const revision = typeof value.revision === 'string' ? value.revision.trim() : ''
  const policy = ROLLOUT_POLICIES[revision]
  if (ROLLOUT_MODES.indexOf(mode) === -1 || !policy ||
    (expectedRevision && revision !== expectedRevision)) {
    throw accessError(`${fieldName} mode or revision is invalid`, 'IAM_V2_ACCESS_INVALID')
  }

  const enforcedActionCodes = normalizedCodeList(
    value.enforced_action_codes,
    normalizeActionCode,
    `${fieldName}.enforced_action_codes`
  )
  const enforcedRouteCodes = normalizedCodeList(
    value.enforced_route_codes,
    code => {
      const normalized = typeof code === 'string' ? code.trim() : ''
      return ROUTE_CODE_PATTERN.test(normalized) ? normalized : ''
    },
    `${fieldName}.enforced_route_codes`
  )
  const grantedActions = actionCodes.reduce((map, code) => {
    map[code] = true
    return map
  }, Object.create(null))
  const availableRoutes = flattenRouteCodes(routes)
  const cardinalityIsValid = enforcedActionCodes.length <= 1 && enforcedRouteCodes.length <= 1
  const pairIsComplete = !policy.pairActionAndRoute ||
    enforcedActionCodes.length === enforcedRouteCodes.length

  if ((mode !== 'v2' && (enforcedActionCodes.length || enforcedRouteCodes.length)) ||
    !onlyKnownCodes(enforcedActionCodes, policy.actionCodes) ||
    !onlyKnownCodes(enforcedRouteCodes, policy.routeCodes) ||
    !cardinalityIsValid ||
    !pairIsComplete ||
    (enforcedActionCodes.length > 0 && policy.allowedDataScopes.indexOf(dataScope) === -1) ||
    enforcedActionCodes.some(code => !grantedActions[code]) ||
    enforcedRouteCodes.some(code => !availableRoutes[code])) {
    throw accessError(`${fieldName} grants are inconsistent`, 'IAM_V2_ACCESS_INVALID')
  }

  return {
    mode,
    revision,
    enforced_action_codes: enforcedActionCodes,
    enforced_route_codes: enforcedRouteCodes
  }
}

function sameAuthorizationRollout (left, right) {
  return left.mode === right.mode && left.revision === right.revision &&
    left.enforced_action_codes.join(',') === right.enforced_action_codes.join(',') &&
    left.enforced_route_codes.join(',') === right.enforced_route_codes.join(',')
}

function normalizeRolloutArray (values, revisions, actionCodes, routes, dataScope, field, deterministic) {
  if (!Array.isArray(values) || values.length !== revisions.length) {
    throw accessError(`${field} must contain every reviewed rollout`, 'IAM_V2_ACCESS_INVALID')
  }

  const byRevision = Object.create(null)
  values.forEach((value, index) => {
    const revision = value && typeof value.revision === 'string' ? value.revision.trim() : ''
    if (revisions.indexOf(revision) === -1 || byRevision[revision] ||
      (deterministic && revision !== revisions[index])) {
      throw accessError(`${field} contains an unknown, duplicate or unordered revision`, 'IAM_V2_ACCESS_INVALID')
    }
    byRevision[revision] = normalizeAuthorizationRollout(
      value,
      actionCodes,
      routes,
      dataScope,
      revision,
      `${field}[${index}]`
    )
  })

  return revisions.map(revision => {
    if (!byRevision[revision]) {
      throw accessError(`${field} is incomplete`, 'IAM_V2_ACCESS_INVALID')
    }
    return byRevision[revision]
  })
}

function normalizeCompatibilityRollouts (source, actionCodes, routes, dataScope) {
  const stage7Alias = normalizeAuthorizationRollout(
    source.authorization_rollout,
    actionCodes,
    routes,
    dataScope,
    STAGE7_ROLLOUT_REVISION,
    'authorization_rollout'
  )

  // Compatibility window: a Stage 7 backend does not have the plural field.
  // Once the field is present it is authoritative and must describe both
  // reviewed batches exactly; malformed or partial arrays fail closed.
  if (source.authorization_rollouts === undefined) {
    return { stage7Alias, rollouts: [stage7Alias] }
  }
  const rollouts = normalizeRolloutArray(
    source.authorization_rollouts,
    COMPATIBILITY_ROLLOUT_REVISIONS,
    actionCodes,
    routes,
    dataScope,
    'authorization_rollouts',
    false
  )
  if (!sameAuthorizationRollout(stage7Alias, rollouts[0])) {
    throw accessError('authorization_rollout is not the Stage 7 compatibility alias', 'IAM_V2_ACCESS_INVALID')
  }
  return { stage7Alias, rollouts }
}

function normalizeAuthorizationRolloutSet (value, compatibility, actionCodes, routes, dataScope) {
  if (value === undefined) return null
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw accessError('authorization_rollout_set must be an object', 'IAM_V2_ACCESS_INVALID')
  }
  const fields = Object.keys(value).sort()
  if (fields.length !== ROLLOUT_SET_FIELDS.length ||
    fields.some((field, index) => field !== ROLLOUT_SET_FIELDS.slice().sort()[index])) {
    throw accessError('authorization_rollout_set fields are invalid', 'IAM_V2_ACCESS_INVALID')
  }
  const revisions = ROLLOUT_SET_SCHEMAS[value.registry_revision]
  if (value.contract_version !== ROLLOUT_SET_CONTRACT_VERSION || !revisions) {
    throw accessError('authorization_rollout_set version is unsupported', 'IAM_V2_ACCESS_INVALID')
  }
  if (compatibility.rollouts.length !== COMPATIBILITY_ROLLOUT_REVISIONS.length) {
    throw accessError('authorization_rollout_set requires the Stage 7/8 compatibility fields', 'IAM_V2_ACCESS_INVALID')
  }

  const rollouts = normalizeRolloutArray(
    value.rollouts,
    revisions,
    actionCodes,
    routes,
    dataScope,
    'authorization_rollout_set.rollouts',
    true
  )
  compatibility.rollouts.forEach((rollout, index) => {
    if (!sameAuthorizationRollout(rollout, rollouts[index])) {
      throw accessError('authorization_rollout_set is inconsistent with compatibility fields', 'IAM_V2_ACCESS_INVALID')
    }
  })

  return {
    contract_version: ROLLOUT_SET_CONTRACT_VERSION,
    registry_revision: value.registry_revision,
    rollouts
  }
}

export function selectAuthorizationRollouts (snapshot) {
  if (snapshot && snapshot.authorization_rollout_set &&
    Array.isArray(snapshot.authorization_rollout_set.rollouts)) {
    return snapshot.authorization_rollout_set.rollouts
  }
  if (snapshot && Array.isArray(snapshot.authorization_rollouts)) {
    return snapshot.authorization_rollouts
  }
  return snapshot && snapshot.authorization_rollout ? [snapshot.authorization_rollout] : []
}

export function aggregateRolloutCodes (rollouts, field) {
  const seen = Object.create(null)
  const result = []
  ;(rollouts || []).forEach(rollout => {
    ;((rollout && rollout[field]) || []).forEach(code => {
      if (!seen[code]) {
        seen[code] = true
        result.push(code)
      }
    })
  })
  return result.sort()
}

export function isFullCatalogRouteSource (snapshot) {
  return hasFullCatalogRouteSource(snapshot)
}

export function selectEnforcedActionCodes (snapshot) {
  if (!snapshot) return []
  if (isFullCatalogRouteSource(snapshot)) return (snapshot.action_codes || []).slice().sort()
  return aggregateRolloutCodes(selectAuthorizationRollouts(snapshot), 'enforced_action_codes')
}

export function selectEnforcedRouteCodes (snapshot) {
  if (!snapshot) return []
  if (isFullCatalogRouteSource(snapshot)) return Object.keys(flattenRouteCodes(snapshot.routes)).sort()
  return aggregateRolloutCodes(selectAuthorizationRollouts(snapshot), 'enforced_route_codes')
}

function normalizeRouteSourceDecision (value) {
  if (value === undefined || value === null) return null
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw accessError('route_source must be an object', 'IAM_V2_ACCESS_INVALID')
  }
  const fields = Object.keys(value).sort()
  if (fields.length !== ROUTE_SOURCE_FIELDS.length ||
    fields.some((field, index) => field !== ROUTE_SOURCE_FIELDS.slice().sort()[index])) {
    throw accessError('route_source fields are invalid', 'IAM_V2_ACCESS_INVALID')
  }
  const mode = typeof value.mode === 'string' ? value.mode.trim() : ''
  const revision = typeof value.revision === 'string' ? value.revision.trim() : ''
  if (mode !== 'v2' || revision !== FULL_CATALOG_ROLLOUT_REVISION) {
    throw accessError('route_source decision is invalid', 'IAM_V2_ACCESS_INVALID')
  }
  return { mode, revision }
}

export function normalizeAccessSnapshot (payload) {
  const source = payload || {}
  const subject = source.subject || {}
  const userID = positiveInteger(subject.user_id)
  const dataScope = positiveInteger(subject.data_scope)
  const generatedAt = positiveInteger(source.generated_at)
  if (!positiveInteger(source.rule_version) || !DIGEST_PATTERN.test(String(source.catalog_digest || '')) ||
    !positiveInteger(source.auth_version) || !userID ||
    !positiveInteger(subject.org_id) || !positiveInteger(subject.system_id) || dataScope < 1 || dataScope > 4 ||
    !generatedAt || generatedAt > currentUnixTime() + 300) {
    throw accessError('IAM V2 access snapshot identity or version is invalid', 'IAM_V2_ACCESS_INVALID')
  }

  const actionCodes = normalizedCodeList(source.action_codes, normalizeActionCode, 'action_codes')
  const routes = normalizeRouteTree(source.routes)
  const authorization = normalizeCompatibilityRollouts(source, actionCodes, routes, dataScope)
  const rolloutSet = normalizeAuthorizationRolloutSet(
    source.authorization_rollout_set,
    authorization,
    actionCodes,
    routes,
    dataScope
  )
  return {
    rule_version: Number(source.rule_version),
    catalog_digest: String(source.catalog_digest),
    auth_version: Number(source.auth_version),
    subject: {
      user_id: userID,
      org_id: Number(subject.org_id),
      system_id: Number(subject.system_id),
      data_scope: dataScope
    },
    role_codes: normalizedCodeList(source.role_codes, normalizeRoleCode, 'role_codes'),
    action_codes: actionCodes,
    // Built-in identity actions are kept separate. can/canAny/canAll never read
    // this collection, so they cannot become business capabilities by accident.
    builtin_action_codes: normalizedCodeList(source.builtin_action_codes, normalizeActionCode, 'builtin_action_codes'),
    routes,
    authorization_rollout: authorization.stage7Alias,
    authorization_rollouts: authorization.rollouts,
    authorization_rollout_set: rolloutSet,
    route_source: normalizeRouteSourceDecision(source.route_source),
    generated_at: generatedAt
  }
}

function defaultState (epoch) {
  return {
    status: STATUS_IDLE,
    snapshot: null,
    error: null,
    epoch: Number(epoch) || 0
  }
}

const state = defaultState(0)

const mutations = {
  START_LOADING (state) {
    state.status = STATUS_LOADING
    state.error = null
  },
  SET_ACCESS (state, snapshot) {
    state.status = STATUS_READY
    state.snapshot = snapshot
    state.error = null
  },
  SET_ERROR (state, error) {
    state.status = STATUS_ERROR
    state.snapshot = null
    state.error = {
      code: String((error && error.code) || 'IAM_V2_ACCESS_UNAVAILABLE'),
      message: String((error && error.message) || error || 'IAM V2 access snapshot is unavailable')
    }
  },
  RESET_ACCESS (state) {
    Object.assign(state, defaultState(state.epoch + 1))
  }
}

const getters = {
  ready: state => state.status === STATUS_READY && Boolean(state.snapshot),
  actionCodes: (state, getters) => getters.ready ? state.snapshot.action_codes : [],
  builtinActionCodes: (state, getters) => getters.ready ? state.snapshot.builtin_action_codes : [],
  routes: (state, getters) => getters.ready ? state.snapshot.routes : [],
  authorizationRollout: (state, getters) => getters.ready ? state.snapshot.authorization_rollout : null,
  authorizationRolloutSet: (state, getters) => getters.ready ? state.snapshot.authorization_rollout_set : null,
  compatibilityAuthorizationRollouts: (state, getters) => getters.ready ? state.snapshot.authorization_rollouts : [],
  authorizationRollouts: (state, getters) => getters.ready ? selectAuthorizationRollouts(state.snapshot) : [],
  enforcedActionCodes: (state, getters) => getters.ready ? selectEnforcedActionCodes(state.snapshot) : [],
  enforcedRouteCodes: (state, getters) => getters.ready ? selectEnforcedRouteCodes(state.snapshot) : [],
  isActionEnforced: (state, getters) => action => getters.ready &&
    getters.enforcedActionCodes.indexOf(normalizeActionCode(action)) !== -1,
  isRouteEnforced: (state, getters) => route => getters.ready &&
    getters.enforcedRouteCodes.indexOf(String(route || '').trim()) !== -1,
  can: (state, getters) => required => getters.ready && can(getters.actionCodes, required),
  canAny: (state, getters) => required => getters.ready && canAny(getters.actionCodes, required),
  canAll: (state, getters) => required => getters.ready && canAll(getters.actionCodes, required)
}

const actions = {
  bootstrap ({ state, commit, dispatch, rootState }, options) {
    const force = Boolean(options && options.force)
    const localUser = rootState.user || {}
    if (inFlightRequest && inFlightRequest.state === state && inFlightRequest.epoch === state.epoch) return inFlightRequest.promise
    if (state.status === STATUS_READY && state.snapshot && !force) return Promise.resolve(state.snapshot)

    commit('START_LOADING')
    const epoch = state.epoch
    const request = getMyAccess().then(payload => {
      const snapshot = normalizeAccessSnapshot(payload)
      if (state.epoch !== epoch) throw accessError('IAM V2 access response belongs to an expired session', 'IAM_V2_ACCESS_STALE')
      // The cookie is the authentication authority and is shared across tabs,
      // while the historical user context lives in tab-local sessionStorage.
      // A new tab or an account switch can therefore leave local identity empty
      // or stale. Reconcile it from the authenticated snapshot before route
      // generation instead of silently mixing one user's local state with
      // another user's token or falling back to Legacy menus.
      const identityChanged = positiveInteger(localUser.user_id) !== snapshot.subject.user_id ||
        positiveInteger(localUser.org_id) !== snapshot.subject.org_id ||
        positiveInteger(localUser.data_scope) !== snapshot.subject.data_scope
      const restoreIdentity = identityChanged
        ? dispatch('RestoreIdentityFromAccess', snapshot.subject, { root: true })
        : Promise.resolve()
      return Promise.resolve(restoreIdentity).then(() => {
        if (state.epoch !== epoch) throw accessError('IAM V2 access response belongs to an expired session', 'IAM_V2_ACCESS_STALE')
        commit('SET_ACCESS', snapshot)
        return snapshot
      })
    }).catch(error => {
      if (state.epoch === epoch) commit('SET_ERROR', error)
      return Promise.reject(error)
    })

    inFlightRequest = { state, epoch, promise: request }
    request.then(() => {
      if (inFlightRequest && inFlightRequest.promise === request) inFlightRequest = null
    }, () => {
      if (inFlightRequest && inFlightRequest.promise === request) inFlightRequest = null
    })
    return request
  },

  refresh ({ dispatch }) {
    return dispatch('bootstrap', { force: true })
  },

  reset ({ commit }) {
    commit('RESET_ACCESS')
    return Promise.resolve()
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}

export {
  STATUS_IDLE,
  STATUS_LOADING,
  STATUS_READY,
  STATUS_ERROR,
  ROLLOUT_SET_CONTRACT_VERSION,
  LEGACY_ROLLOUT_SET_REGISTRY_REVISION,
  ROLLOUT_SET_REGISTRY_REVISION,
  FULL_CATALOG_ROLLOUT_REVISION,
  LEGACY_ROLLOUT_SET_REVISIONS,
  ROLLOUT_SET_REVISIONS
}



// WEBPACK FOOTER //
// ./src/store/modules/access.js